import React, { useContext, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReportDetailsCard from "./ReportDetailsCard";
import ReportDownloadActions from "./ReportDownloadActions";
import { EcgGraphDialog, EcgPreviewCard } from "./EcgGraphCards";
import {
  Interpretation,
  PatientSummaryCard,
  SendForInterpretationAccess,
} from "./ReportSections";
import { parseDeviceMeta } from "./reportUtils";
import { FULL_REPORT_SOURCE } from "../../../analytics";
import { AuthContext } from "../../../context/AuthContextProvider";

const ACTION_BUTTON_SX = {
  height: "46px",
  borderRadius: "9px",
  padding: "14px 12px",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  gap: "9px",
  fontWeight: "600",
  boxShadow: "none",
  fontFamily: "figtree",
  fontSize: "15px",
};

const SEND_BUTTON_SX = {
  ...ACTION_BUTTON_SX,
  width: "190px",
  backgroundColor: "#4F46E5",
  color: "var(--app-unread)",
  "&:hover": { backgroundColor: "none", boxShadow: "none" },
};

const VIEW_REPORT_BUTTON_SX = {
  ...ACTION_BUTTON_SX,
  width: "155px",
  backgroundColor: "var(--app-unread)",
  color: "#4F46E5",
  "&:hover": { backgroundColor: "#E8EBFF", boxShadow: "none" },
};

const FOOTER_SX = {
  position: "absolute",
  bottom: 0,
  left: "34%",
  width: "65%",
  minHeight: "74px",
  px: "15px",
  py: "14px",
  backgroundColor: "var(--app-surface)",
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  flexWrap: "nowrap",
  overflowX: "auto",
  whiteSpace: "nowrap",
};

const ShortReport = ({
  Link,
  setCreateModal,
  data,
  ecgGraph,
  isLoadingGraph,
  reportData,
}) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { user } = useContext(AuthContext);
  // Migrated businesses don't get to send reports for interpretation -
  // userType stays plain "b2b", migration is a separate flag.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;

  const deviceMeta = useMemo(
    () => parseDeviceMeta(data?.meta_data),
    [data?.meta_data],
  );

  return (
    <>
      <Box
        className="custom-scroll"
        sx={{
          maxHeight: "60vh",
          overflowY: "auto",
          paddingBottom: "50px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                fontFamily: "Figtree",
                fontWeight: 700,
                fontSize: "18px !important",
              }}
            >
              Preview
            </Typography>
          </Box>

          <ReportDownloadActions
            data={data}
            ecgGraph={ecgGraph}
            isLoadingGraph={isLoadingGraph}
          />
        </Box>

        <PatientSummaryCard type="short" data={data} loading={!data} />

        <Box>
          <Interpretation type="short" data={data} />
        </Box>
        <Box>
          <ReportDetailsCard type="interpretation" data={data} />
        </Box>

        {data?.interpretation_request_status &&
          data?.interpretation_request_status !== "completed" && <br />}
        <br />

        <EcgPreviewCard
          data={data}
          ecgGraph={ecgGraph}
          isLoadingGraph={isLoadingGraph}
          deviceMeta={deviceMeta}
          LinkComponent={Link}
          onExpand={() => setOpenModal(true)}
        />

        <ReportDetailsCard type="parameters" data={data} />

        <Box sx={FOOTER_SX}>
          {data && data?.interpretation_request_status == null && !isMigratedUser && (
            <SendForInterpretationAccess>
              <Button sx={SEND_BUTTON_SX} onClick={() => setCreateModal(true)}>
                Send for Interpretation
              </Button>
            </SendForInterpretationAccess>
          )}
          {reportData > 0 ? (
            <Button
              // The full report screen reports source=direct unless it is told
              // otherwise, so only this preview-pane route marks itself.
              onClick={() =>
                navigate(`/ecg-reports/${data?.id}`, {
                  state: { analyticsSource: FULL_REPORT_SOURCE.PREVIEW },
                })
              }
              sx={VIEW_REPORT_BUTTON_SX}
            >
              View Full Report
            </Button>
          ) : null}
        </Box>
      </Box>

      <EcgGraphDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        data={data}
        ecgGraph={ecgGraph}
        isLoadingGraph={isLoadingGraph}
        deviceMeta={deviceMeta}
      />
    </>
  );
};

export default ShortReport;
